import { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import {
  Radar, Gauge, Users, Building2, ListChecks, Package, Settings as SettingsIcon,
  Menu, X, ShieldCheck, LogOut,
} from "lucide-react";
import { useOperator } from "@/context/OperatorContext";

const LINKS = [
  { to: "/", label: "Command Deck", icon: Radar, code: "CMD" },
  { to: "/treasury", label: "Treasury", icon: Gauge, code: "TRS" },
  { to: "/crew", label: "Crew", icon: Users, code: "CRW" },
  { to: "/clients", label: "Clients", icon: Building2, code: "CLT" },
  { to: "/jobs", label: "Jobs", icon: ListChecks, code: "JOB" },
  { to: "/catalog", label: "Catalog", icon: Package, code: "CAT" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, code: "SET" },
];

export default function Shell({ children }) {
  const [drawer, setDrawer] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      <CommandBar onMenu={() => setDrawer(true)} />
      <NavStrip />
      <Drawer open={drawer} onClose={() => setDrawer(false)} />
      <main className="flex-1 w-full max-w-[1280px] mx-auto px-3 sm:px-5 py-5 sm:py-7">
        {children}
      </main>
      <Footer />
    </div>
  );
}

function CommandBar({ onMenu }) {
  return (
    <header
      className="sticky top-0 z-40 backdrop-blur"
      style={{ background: "rgba(7,11,24,0.86)", borderBottom: "1px solid var(--yb-hairline)" }}
    >
      <div className="max-w-[1280px] mx-auto h-14 px-3 sm:px-5 flex items-center gap-3">
        <button
          type="button"
          onClick={onMenu}
          aria-label="Open navigation"
          className="lg:hidden w-9 h-9 rounded flex items-center justify-center hover:bg-white/5"
          style={{ border: "1px solid var(--yb-hairline)", color: "var(--yb-text-dim)" }}
        >
          <Menu size={18} />
        </button>

        <NavLink to="/" className="flex items-center gap-2.5">
          <span
            className="w-8 h-8 rounded flex items-center justify-center font-display font-bold"
            style={{ background: "var(--yb-cyan)", color: "#04101b", boxShadow: "0 0 20px -4px var(--yb-cyan)" }}
          >
            Y
          </span>
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-[15px] tracking-[0.14em]" style={{ color: "var(--yb-text)" }}>
              YABBAI
            </span>
            <span className="font-head text-[9px] tracking-[0.42em]" style={{ color: "var(--yb-text-muted)" }}>
              ORCHESTRA
            </span>
          </span>
        </NavLink>

        <div className="hidden md:flex items-center gap-2 ml-3 px-3 h-7 rounded-full"
             style={{ border: "1px solid var(--yb-hairline)" }}>
          <span className="dot yb-pulse" style={{ color: "var(--yb-mint)", width: 6, height: 6 }} />
          <span className="font-mono text-[10px] tracking-[0.2em]" style={{ color: "var(--yb-text-muted)" }}>
            BURN CITY · MELBOURNE AU
          </span>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <NavLink
            to="/gold"
            className="pill hover:bg-white/5"
            style={{ color: "var(--yb-amber)" }}
            title="Open the legacy Gold-Hunter app"
          >
            ⛏ Gold-Hunter
          </NavLink>
          <OperatorChip />
        </div>
      </div>
    </header>
  );
}

// The Shell only ever renders behind AuthGate, so the operator here is always
// fully authenticated — the chip identifies who, and offers the way out.
function OperatorChip() {
  const { email, logout } = useOperator();
  return (
    <div className="flex items-center gap-2">
      <span
        className="pill"
        style={{ color: "var(--yb-mint)" }}
        title={email ? `Signed in as ${email}` : "Signed in"}
      >
        <ShieldCheck size={12} />
        <span className="hidden sm:inline">{email || "Operator"}</span>
      </span>
      <button
        type="button"
        onClick={logout}
        aria-label="Sign out"
        className="w-8 h-8 rounded flex items-center justify-center hover:bg-white/5"
        style={{ border: "1px solid var(--yb-hairline)", color: "var(--yb-text-muted)" }}
      >
        <LogOut size={15} />
      </button>
    </div>
  );
}

function NavStrip() {
  return (
    <nav
      className="hidden lg:block sticky top-14 z-30 backdrop-blur"
      style={{ background: "rgba(7,11,24,0.7)", borderBottom: "1px solid var(--yb-hairline)" }}
    >
      <div className="max-w-[1280px] mx-auto px-5 flex items-center gap-1">
        {LINKS.map(({ to, label, icon: Icon, code }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            data-testid={`nav-${code.toLowerCase()}`}
            className="relative group flex items-center gap-2 px-4 h-11 font-head text-[13px] transition-colors"
            style={({ isActive }) => ({ color: isActive ? "var(--yb-cyan)" : "var(--yb-text-dim)" })}
          >
            {({ isActive }) => (
              <>
                <Icon size={15} />
                {label}
                {isActive && (
                  <span className="absolute left-2 right-2 bottom-0 h-[2px]"
                        style={{ background: "var(--yb-cyan)", boxShadow: "0 0 12px var(--yb-cyan)" }} />
                )}
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

function Drawer({ open, onClose }) {
  const closeRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 z-50" style={{ background: "rgba(3,6,15,0.7)" }} onClick={onClose} aria-hidden="true" />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        className="fixed top-0 left-0 bottom-0 z-50 w-[280px] p-3"
        style={{ background: "var(--yb-panel)", borderRight: "1px solid var(--yb-hairline)" }}
      >
        <div className="flex items-center justify-between h-12 px-2">
          <span className="font-display text-[15px] tracking-[0.14em]" style={{ color: "var(--yb-cyan)" }}>YABBAI</span>
          <button ref={closeRef} onClick={onClose} aria-label="Close navigation"
                  className="w-9 h-9 rounded flex items-center justify-center hover:bg-white/5"
                  style={{ border: "1px solid var(--yb-hairline)", color: "var(--yb-text-dim)" }}>
            <X size={18} />
          </button>
        </div>
        <nav className="mt-2 space-y-1">
          {LINKS.map(({ to, label, icon: Icon, code }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              onClick={onClose}
              className="flex items-center gap-3 h-11 px-3 rounded font-head text-[14px]"
              style={({ isActive }) => ({
                color: isActive ? "var(--yb-cyan)" : "var(--yb-text-dim)",
                background: isActive ? "rgba(54,230,255,0.08)" : "transparent",
                border: isActive ? "1px solid var(--yb-hairline)" : "1px solid transparent",
              })}
            >
              <Icon size={16} />
              {label}
              <span className="ml-auto font-mono text-[9px] tracking-widest" style={{ color: "var(--yb-text-muted)" }}>{code}</span>
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  );
}

function Footer() {
  return (
    <footer className="py-6 text-center" style={{ borderTop: "1px solid var(--yb-hairline)" }}>
      <div className="font-mono text-[11px] tracking-[0.18em]" style={{ color: "var(--yb-text-muted)" }}>
        YABBAI ORCHESTRA · SUPERVISED AGENCY OS · HUMAN-IN-THE-LOOP · NO AUTONOMOUS FUNDS
      </div>
    </footer>
  );
}
